import { BREAKOUT_LIMITS, type BreakoutRoom } from '../../shared/protocol';

// Planning helpers for breakout rooms (pure: unit-tested in breakout.test.ts).

export type AssignMode = 'random' | 'manual' | 'choose';

export function makeRooms(count: number): BreakoutRoom[] {
  const n = Math.max(1, Math.min(BREAKOUT_LIMITS.rooms, Math.floor(count) || 1));
  return Array.from({ length: n }, (_, i) => ({ id: `r${i + 1}`, name: `Sala ${i + 1}` }));
}

/** A new room after the existing ones, with an id and a "Sala N" name not used yet. */
export function addRoom(rooms: BreakoutRoom[]): BreakoutRoom[] {
  if (rooms.length >= BREAKOUT_LIMITS.rooms) return rooms;
  let n = rooms.length + 1;
  const ids = new Set(rooms.map((r) => r.id));
  const names = new Set(rooms.map((r) => r.name));
  while (ids.has(`r${n}`) || names.has(`Sala ${n}`)) n++;
  return [...rooms, { id: `r${n}`, name: `Sala ${n}` }];
}

/**
 * Shuffles people and deals them round-robin, so room sizes differ by at most one
 * (Zoom's "Assign automatically").
 */
export function assignRandom(people: string[], rooms: BreakoutRoom[], random: () => number = Math.random): Record<string, string> {
  if (!rooms.length) return {};
  const order = [...people];
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  const out: Record<string, string> = {};
  order.forEach((id, i) => (out[id] = rooms[i % rooms.length].id));
  return out;
}

/** Suggested number of rooms for a group size: about four people per room, at least two rooms. */
export function suggestedRooms(people: number): number {
  if (people < 2) return 1;
  return Math.max(2, Math.min(BREAKOUT_LIMITS.rooms, Math.round(people / 4)));
}
